/**
 * Друк «Журналу доходів і витрат» ФОП-єдинника (ч.1376).
 *
 * Графи — як у типовій формі книги обліку доходів і витрат: отримано (готівкова / безготівкова /
 * натуральна форма), повернуто, дохід (отримано мінус повернуто), безоплатно отримане,
 * кредиторська заборгованість з минулим строком давності, разом дохід; витрати — товари,
 * роботи, послуги / оплата праці / єдиний внесок / інші. Рядок — день, у якому був рух.
 */
import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { incomeJournal, type JournalRow } from '@/api/incomeJournal';
import PrintActions from '@/shared/components/PrintActions';
import { partyCodesText } from '@/shared/utils/partyCodes';
import { extractApiError } from '@/utils/extractApiError';

const fmt = (v: number) => (v ? v.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '');
const dmy = (iso: string) => iso.slice(0, 10).split('-').reverse().join('.');
const GROUPS: Record<string, string> = { FOP_1: '1 група', FOP_2: '2 група', FOP_3_5: '3 група, 5 %' };

interface IncomeDay { date: string; cash: number; noncash: number; inKind: number; refund: number;
  free: number; payable: number }
interface ExpenseDay { date: string; goods: number; salary: number; esv: number; other: number }

/** Рядки журналу → суми по днях за графами (лише те, що є доходом / витратою). */
export function journalDays(rows: JournalRow[]): { income: IncomeDay[]; expenses: ExpenseDay[] } {
  const inc = new Map<string, IncomeDay>();
  const exp = new Map<string, ExpenseDay>();
  for (const r of rows) {
    const a = Number(r.amount);
    if (r.section === 'INCOME') {
      const d = inc.get(r.date) ?? { date: r.date, cash: 0, noncash: 0, inKind: 0, refund: 0, free: 0, payable: 0 };
      if (r.kind === 'RECEIVED') {
        if (r.payment_form === 'CASH') d.cash += a;
        else if (r.payment_form === 'IN_KIND') d.inKind += a;
        else d.noncash += a;
      } else if (r.kind === 'REFUND') d.refund += a;
      else if (r.kind === 'FREE_RECEIVED') d.free += a;
      else if (r.kind === 'PAYABLE_EXPIRED') d.payable += a;
      else continue;
      inc.set(r.date, d);
    } else {
      const d = exp.get(r.date) ?? { date: r.date, goods: 0, salary: 0, esv: 0, other: 0 };
      const key = ({ GOODS: 'goods', SALARY: 'salary', ESV: 'esv' } as const)[r.kind as 'GOODS' | 'SALARY' | 'ESV'] ?? 'other';
      d[key] += a;
      exp.set(r.date, d);
    }
  }
  const byDate = <T extends { date: string }>(m: Map<string, T>) => [...m.values()].sort((x, y) => x.date.localeCompare(y.date));
  return { income: byDate(inc), expenses: byDate(exp) };
}

const cell: React.CSSProperties = { border: '1px solid #000', padding: '2px 4px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
const head: React.CSSProperties = { border: '1px solid #000', padding: '2px 4px', textAlign: 'center', fontWeight: 400, verticalAlign: 'middle' };

export default function IncomeJournalPrintPage() {
  const [sp] = useSearchParams();
  const orgId = sp.get('org') ?? '';
  const from = sp.get('from') ?? '';
  const to = sp.get('to') ?? '';
  const q = useQuery({
    queryKey: ['income-journal', orgId, { date_from: from, date_to: to }],
    queryFn: () => incomeJournal.get({ organization_id: orgId, date_from: from, date_to: to }),
    enabled: !!orgId && !!from && !!to,
  });
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);
  const days = useMemo(() => journalDays(q.data?.rows ?? []), [q.data]);

  if (q.isError) return <div style={{ padding: '2rem' }} className="error">{extractApiError(q.error)}</div>;
  if (!q.data) return <div style={{ padding: '2rem' }}>Завантаження…</div>;
  const j = q.data;
  const org = orgsQ.data?.items.find((o) => o.id === orgId);
  const sum = <T,>(list: T[], f: (x: T) => number) => list.reduce((s, x) => s + f(x), 0);
  const inc = days.income;
  const exp = days.expenses;
  const net = (d: IncomeDay) => d.cash + d.noncash + d.inKind - d.refund;
  const total = (d: IncomeDay) => net(d) + d.free + d.payable;
  const expTotal = (d: ExpenseDay) => d.goods + d.salary + d.esv + d.other;

  return (
    <div className="print-page" style={{
      padding: '12mm', fontFamily: 'Times, serif', fontSize: '9.5pt', maxWidth: '297mm', margin: '0 auto',
      background: 'var(--bg-panel)', lineHeight: 1.35, color: '#000',
    }}>
      <PrintActions />
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontWeight: 700 }}>{org?.full_name ?? org?.name ?? ''}</div>
        {org && <div style={{ fontSize: '9pt' }}>{partyCodesText(org)}{GROUPS[j.organization.tax_system]
          ? `, платник єдиного податку ${GROUPS[j.organization.tax_system]}` : ''}</div>}
      </div>
      <h2 style={{ textAlign: 'center', fontSize: '13pt', margin: '0.8rem 0 0.2rem' }}>ЖУРНАЛ ДОХОДІВ І ВИТРАТ</h2>
      <p style={{ textAlign: 'center', margin: '0 0 0.8rem' }}>за період з {dmy(from)} по {dmy(to)}</p>

      <h3 style={{ fontSize: '10.5pt', margin: '0.4rem 0' }}>Доходи, грн</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={head} rowSpan={2}>Дата</th>
            <th style={head} colSpan={3}>Сума отриманого доходу</th>
            <th style={head} rowSpan={2}>Сума повернутих коштів</th>
            <th style={head} rowSpan={2}>Дохід від діяльності (гр. 2–4 мінус гр. 5)</th>
            <th style={head} rowSpan={2}>Безоплатно отримані товари, роботи, послуги</th>
            <th style={head} rowSpan={2}>Кредиторська заборгованість з минулим строком давності</th>
            <th style={head} rowSpan={2}>Разом дохід</th>
          </tr>
          <tr>
            <th style={head}>готівкова</th><th style={head}>безготівкова</th><th style={head}>натуральна</th>
          </tr>
          <tr>{[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => <th key={n} style={head}>{n}</th>)}</tr>
        </thead>
        <tbody>
          {inc.length === 0 && <tr><td style={{ ...cell, textAlign: 'center' }} colSpan={9}>Доходів за період немає</td></tr>}
          {inc.map((d) => (
            <tr key={d.date}>
              <td style={{ ...cell, textAlign: 'center' }}>{dmy(d.date)}</td>
              <td style={cell}>{fmt(d.cash)}</td><td style={cell}>{fmt(d.noncash)}</td><td style={cell}>{fmt(d.inKind)}</td>
              <td style={cell}>{fmt(d.refund)}</td><td style={cell}>{fmt(net(d))}</td>
              <td style={cell}>{fmt(d.free)}</td><td style={cell}>{fmt(d.payable)}</td><td style={cell}>{fmt(total(d))}</td>
            </tr>
          ))}
          <tr style={{ fontWeight: 700 }}>
            <td style={{ ...cell, textAlign: 'center' }}>Разом</td>
            <td style={cell}>{fmt(sum(inc, (d) => d.cash))}</td><td style={cell}>{fmt(sum(inc, (d) => d.noncash))}</td>
            <td style={cell}>{fmt(sum(inc, (d) => d.inKind))}</td><td style={cell}>{fmt(sum(inc, (d) => d.refund))}</td>
            <td style={cell}>{fmt(sum(inc, net))}</td><td style={cell}>{fmt(sum(inc, (d) => d.free))}</td>
            <td style={cell}>{fmt(sum(inc, (d) => d.payable))}</td><td style={cell}>{fmt(sum(inc, total))}</td>
          </tr>
        </tbody>
      </table>

      <h3 style={{ fontSize: '10.5pt', margin: '1rem 0 0.4rem' }}>Витрати, грн</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={head}>Дата</th><th style={head}>Товари, роботи, послуги</th><th style={head}>Оплата праці</th>
            <th style={head}>Єдиний внесок</th><th style={head}>Інші витрати</th><th style={head}>Разом витрати</th>
          </tr>
          <tr>{[1, 2, 3, 4, 5, 6].map((n) => <th key={n} style={head}>{n}</th>)}</tr>
        </thead>
        <tbody>
          {exp.length === 0 && <tr><td style={{ ...cell, textAlign: 'center' }} colSpan={6}>Витрат за період немає</td></tr>}
          {exp.map((d) => (
            <tr key={d.date}>
              <td style={{ ...cell, textAlign: 'center' }}>{dmy(d.date)}</td>
              <td style={cell}>{fmt(d.goods)}</td><td style={cell}>{fmt(d.salary)}</td>
              <td style={cell}>{fmt(d.esv)}</td><td style={cell}>{fmt(d.other)}</td><td style={cell}>{fmt(expTotal(d))}</td>
            </tr>
          ))}
          <tr style={{ fontWeight: 700 }}>
            <td style={{ ...cell, textAlign: 'center' }}>Разом</td>
            <td style={cell}>{fmt(sum(exp, (d) => d.goods))}</td><td style={cell}>{fmt(sum(exp, (d) => d.salary))}</td>
            <td style={cell}>{fmt(sum(exp, (d) => d.esv))}</td><td style={cell}>{fmt(sum(exp, (d) => d.other))}</td>
            <td style={cell}>{fmt(sum(exp, expTotal))}</td>
          </tr>
        </tbody>
      </table>

      <p style={{ marginTop: '1.5rem' }}>
        Дохід з початку року по {dmy(to)}: <strong>{Number(j.year.income).toLocaleString('uk-UA', { minimumFractionDigits: 2 })} грн</strong>
      </p>
      <p style={{ marginTop: '1.5rem' }}>Фізична особа — підприємець ____________________ {org?.director_name ?? ''}</p>
    </div>
  );
}
